import { cn } from "@/lib/cn";
import type { Semantic } from "@/lib/status";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

export interface DataTableColumn {
  mono?: boolean;
}

interface DataTableV2Props<T> {
  data: T[];
  columns: (ColumnDef<T, unknown> & DataTableColumn)[];
  onRowClick?: (row: T) => void;
  getRowAccent?: (row: T) => Semantic | undefined;
  caption?: string;
  sortLabel?: string;
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (next: number) => void;
}

const accentColors: Record<Semantic, string> = {
  success: "bg-[#10B981]",
  info: "bg-[#0EA5E9]",
  error: "bg-[#F43F5E]",
  warning: "bg-[#F59E0B]",
  neutral: "bg-[#CBD5DF]",
};

export function DataTableV2<T extends { id?: string }>({
  data,
  columns,
  onRowClick,
  getRowAccent,
  caption = "All items",
  sortLabel = "sorted · created desc",
  page,
  pageSize,
  total,
  onPageChange,
}: DataTableV2Props<T>) {
  const table = useReactTable({
    data,
    columns: columns as ColumnDef<T, unknown>[],
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-surface shadow-[0_1px_2px_rgba(11,20,38,0.04),0_12px_30px_rgba(11,20,38,0.06)]">
      <div className="flex items-center justify-between border-b border-line px-[18px] py-[13px]">
        <span className="text-[13px] font-semibold text-ink">{caption}</span>
        <span className="font-mono text-[11px] font-medium text-muted">{sortLabel}</span>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-line">
            {getRowAccent && <th className="w-[5px] p-0" />}
            {table.getHeaderGroups()[0]?.headers.map((header) => (
              <th
                key={header.id}
                className="px-3 py-3 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-navy-300"
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const accent = getRowAccent?.(row.original);
            return (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && onRowClick) {
                    onRowClick(row.original);
                  }
                }}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? "link" : undefined}
                aria-label={
                  onRowClick
                    ? String(
                        (row.original as Record<string, unknown>).name ??
                          (row.original as Record<string, unknown>).id ??
                          "",
                      )
                    : undefined
                }
                className={cn(
                  "border-b border-[#F0F1F3] last:border-b-0 transition-colors",
                  onRowClick &&
                    "cursor-pointer hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-transparent",
                )}
              >
                {getRowAccent && (
                  <td className="w-[5px] p-0">
                    <div className={cn("h-full w-[5px]", accent ? accentColors[accent] : "")} />
                  </td>
                )}
                {row.getVisibleCells().map((cell) => {
                  const colDef = cell.column.columnDef as ColumnDef<T, unknown> & DataTableColumn;
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-3 py-[13px] text-[13px]",
                        colDef.mono && "font-mono text-xs text-muted tabular-nums",
                      )}
                    >
                      {flexRender(colDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {page !== undefined && pageSize !== undefined && total !== undefined && total > pageSize && (
        <div className="flex items-center justify-between border-t border-line px-[18px] py-[11px] font-mono text-[11px] text-muted">
          <span>
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
              className="rounded-md border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink transition-colors hover:bg-code-bg disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              aria-label="Next page"
              disabled={page * pageSize >= total}
              onClick={() => onPageChange?.(page + 1)}
              className="rounded-md border border-line bg-surface px-2.5 py-1 text-[11px] font-medium text-ink transition-colors hover:bg-code-bg disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
